export const RECAPTCHA_SITE_KEY = __RECAPTCHA_SITE_KEY__.trim()

export type RecaptchaAction = 'login' | 'register'

type GrecaptchaEnterprise = {
  ready(cb: () => void): void
  execute(siteKey: string, options: { action: string }): Promise<string>
}

declare global {
  interface Window {
    grecaptcha?: {
      enterprise?: GrecaptchaEnterprise
    }
  }
}

let loadPromise: Promise<void> | null = null

function getEnterprise(): GrecaptchaEnterprise | undefined {
  return window.grecaptcha?.enterprise
}

function waitForEnterpriseReady(timeoutMs = 15000): Promise<GrecaptchaEnterprise> {
  return new Promise((resolve, reject) => {
    const started = Date.now()
    const tick = () => {
      const enterprise = getEnterprise()
      if (typeof enterprise?.ready === 'function') {
        resolve(enterprise)
        return
      }
      if (Date.now() - started >= timeoutMs) {
        reject(new Error('reCAPTCHA is unavailable'))
        return
      }
      window.setTimeout(tick, 50)
    }
    tick()
  })
}

function loadEnterpriseScript(): Promise<void> {
  if (loadPromise) return loadPromise

  if (typeof window === 'undefined') {
    return Promise.reject(new Error('reCAPTCHA is only available in the browser'))
  }

  hideRecaptchaBadge()

  if (typeof getEnterprise()?.ready === 'function') {
    loadPromise = Promise.resolve()
    return loadPromise
  }

  loadPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-recaptcha="enterprise"]')
    if (existing) {
      if (typeof getEnterprise()?.ready === 'function') {
        resolve()
        return
      }
      existing.addEventListener('load', () => resolve(), { once: true })
      existing.addEventListener(
        'error',
        () => {
          loadPromise = null
          reject(new Error('Failed to load reCAPTCHA'))
        },
        { once: true },
      )
      return
    }

    if (!RECAPTCHA_SITE_KEY) {
      loadPromise = null
      reject(new Error('RECAPTCHA_SITE_KEY is not configured'))
      return
    }

    const script = document.createElement('script')
    script.src = `https://www.recaptcha.net/recaptcha/enterprise.js?render=${encodeURIComponent(RECAPTCHA_SITE_KEY)}`
    script.async = true
    script.dataset.recaptcha = 'enterprise'
    script.onload = () => resolve()
    script.onerror = () => {
      loadPromise = null
      reject(new Error('Failed to load reCAPTCHA'))
    }
    document.head.appendChild(script)
  })

  return loadPromise
}

function hideRecaptchaBadge(): void {
  if (typeof document === 'undefined') return
  if (document.getElementById('recaptcha-badge-hidden')) return
  const style = document.createElement('style')
  style.id = 'recaptcha-badge-hidden'
  style.textContent = '.grecaptcha-badge{visibility:hidden!important}'
  document.head.appendChild(style)
}

const MAX_EXECUTE_ATTEMPTS = 3
const EXECUTE_RETRY_DELAY_MS = 400

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms)
  })
}

export function isRecaptchaBrowserError(value: unknown): boolean {
  if (typeof value === 'string') return /\bBROWSER_ERROR\b/i.test(value)
  if (!value || typeof value !== 'object') return false
  try {
    return /\bBROWSER_ERROR\b/i.test(JSON.stringify(value))
  } catch {
    return false
  }
}

// function isRetryableCaptchaResponse(value: unknown): boolean {
//   if (isRecaptchaBrowserError(value)) return true
//   if (!value || typeof value !== 'object') return false
//   const code = (value as { code?: unknown }).code
//   return typeof code === 'string' && code.toUpperCase() === 'CAPTCHA_FAILED'
// }

function isRetryableCaptchaResponse(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false

  const payload = value as {
    code?: unknown
    reason?: unknown
    invalidReason?: unknown
  }

  return (
    payload.code === 'CAPTCHA_FAILED' &&
    payload.reason === 'INVALID_TOKEN' &&
    payload.invalidReason === 'BROWSER_ERROR'
  )
}

async function executeFreshToken(
  enterprise: GrecaptchaEnterprise,
  action: RecaptchaAction,
): Promise<string> {
  let lastError: Error | null = null

  for (let attempt = 1; attempt <= MAX_EXECUTE_ATTEMPTS; attempt++) {
    try {
      const token = await enterprise.execute(RECAPTCHA_SITE_KEY, { action })
      if (typeof token === 'string' && token.trim() && !isRecaptchaBrowserError(token)) {
        return token
      }
      lastError = new Error('reCAPTCHA BROWSER_ERROR')
    } catch (error) {
      lastError = error instanceof Error ? error : new Error('reCAPTCHA BROWSER_ERROR')
    }

    if (attempt < MAX_EXECUTE_ATTEMPTS) {
      await delay(EXECUTE_RETRY_DELAY_MS * attempt)
    }
  }

  throw lastError ?? new Error('reCAPTCHA BROWSER_ERROR')
}

/** Load enterprise.js as soon as the auth page mounts; call execute() only on submit. */
export function preloadRecaptcha(): void {
  if (loadPromise) return
  void loadEnterpriseScript().catch(() => {})
}

export async function getRecaptchaToken(action: RecaptchaAction): Promise<string> {
  // Reuse preload's in-flight/completed promise — do not start a second load.
  await (loadPromise ?? loadEnterpriseScript())
  const enterprise = await waitForEnterpriseReady()

  await new Promise<void>((resolve) => {
    enterprise.ready(() => resolve())
  })

  if (typeof enterprise.execute !== 'function') {
    throw new Error('reCAPTCHA is unavailable')
  }

  return executeFreshToken(enterprise, action)
}

export async function fetchWithRecaptchaRetry(
  action: RecaptchaAction,
  send: (captchaToken: string) => Promise<Response>,
): Promise<Response> {
  let response: Response | undefined

  for (let attempt = 1; attempt <= MAX_EXECUTE_ATTEMPTS; attempt++) {
    const captchaToken = await getRecaptchaToken(action)
    response = await send(captchaToken)
    if (response.ok) return response

    const payload = await response.clone().json().catch(() => null)
    if (!isRetryableCaptchaResponse(payload) || attempt === MAX_EXECUTE_ATTEMPTS) {
      return response
    }
    await delay(EXECUTE_RETRY_DELAY_MS * attempt)
  }

  return response as Response
}
